import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import MobileHeader from '../../components/feature/MobileHeader';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

interface Game {
  id: string;
  lottery_type: string;
  numbers: number[];
  contest?: string;
  draw_id?: number;
  status: 'pending' | 'checked' | 'expired';
  hits?: number;
  hits_numbers?: number[];
  prize?: number;
  created_at: string;
  checked_at?: string;
  extra?: any;
}

const lotteryNames = {
  'mega-sena': 'Mega-Sena',
  'quina': 'Quina',
  'lotofacil': 'Lotofácil',
  'lotomania': 'Lotomania',
  'timemania': 'Timemania',
  'dia-de-sorte': 'Dia de Sorte',
  'super-sete': 'Super Sete',
  'dupla-sena': 'Dupla Sena',
  'federal': 'Loteria Federal'
};

export default function MeusJogos() {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [contestModal, setContestModal] = useState<{ open: boolean; game?: Game | null } | null>(null);
  const [contestInput, setContestInput] = useState<string>('');
  const [checkFeedback, setCheckFeedback] = useState<Record<string, { hits: number; status: string; prize?: number }>>({});
  const [filter, setFilter] = useState({
    lottery_type: '',
    status: '',
    date_from: '',
    date_to: ''
  });
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    loadGames();
  }, [filter]);

  const loadGames = async () => {
    try {
      setLoading(true);
      const response = await apiService.getGames(filter);
      setGames(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar jogos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (game: Game) => {
    try {
      await apiService.duplicateGame(game.id);
      loadGames();
    } catch (error) {
      console.error('Erro ao duplicar jogo:', error);
    }
  };

  const handleCheckNow = async (game: Game) => {
    try {
      const r = await apiService.checkGame(game.id);
      const hits = Number(r?.best_hits ?? r?.hits ?? 0);
      const prize = typeof r?.prize_amount !== 'undefined' ? Number(r.prize_amount) : undefined;
      setCheckFeedback(prev => ({ ...prev, [game.id]: { hits, status: String(r?.status || 'checked'), prize } }));
      setTimeout(() => setCheckFeedback(prev => { const cp = { ...prev }; delete cp[game.id]; return cp; }), 6000);
      await loadGames();
    } catch (error) {
      const msg = apiService.extractErrorMessage(error);
      alert(msg || 'Falha ao conferir jogo');
    }
  };

  const generatePDF = async (game: Game) => {
    try {
      const pdfBlob = await apiService.getGamePDF(game.id);
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    }
  };

  const deleteGame = async (game: Game) => {
    try {
      await apiService.deleteGame(game.id);
      setSelected(prev => { const cp = { ...prev }; delete cp[game.id]; return cp; });
      loadGames();
    } catch (error) {
      console.error('Erro ao excluir jogo:', error);
      alert('Falha ao excluir jogo');
    }
  };

  const generateSelectedPDF = async () => {
    const ids = Object.keys(selected).filter((id) => selected[id]);
    if (ids.length === 0) {
      alert('Selecione ao menos um jogo');
      return;
    }
    try {
      const pdfBlob = await apiService.getGamesPDFBatch(ids);
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      const msg = apiService.extractErrorMessage(error);
      alert(msg || 'Falha ao gerar PDF dos selecionados');
    }
  };

  const saveContest = async () => {
    if (!contestModal?.game) return;
    const dn = contestInput.trim();
    if (!dn) { alert('Informe o concurso'); return; }
    try {
      await apiService.assignContest(contestModal.game.id, dn);
      setContestModal({ open: false, game: null });
      setContestInput('');
      await loadGames();
    } catch (error) {
      const msg = apiService.extractErrorMessage(error);
      alert(msg || 'Falha ao atribuir concurso');
    }
  };

  const checkSelected = async () => {
    const ids = Object.keys(selected).filter((id) => selected[id]);
    if (ids.length === 0) { alert('Selecione jogos com concurso'); return; }
    const idsWithContest = ids.filter(id => {
      const g = games.find(x => x.id === id);
      return !!(g && (g.draw_id || g.contest));
    });
    if (idsWithContest.length === 0) { alert('Selecione ao menos um jogo com concurso definido'); return; }
    try {
      await apiService.checkGamesBatch(idsWithContest);
      await loadGames();
    } catch (error) {
      const msg = apiService.extractErrorMessage(error);
      alert(msg || 'Falha ao conferir jogos');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'checked': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'expired': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'checked': return 'Conferido';
      case 'pending': return 'Pendente';
      case 'expired': return 'Expirado';
      default: return 'Desconhecido';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <Sidebar />
      <MobileHeader />
      
      <div className="md:ml-72 p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold mb-2">Meus Jogos</h1>
            <p className="text-white/60 text-sm">Gerencie seus bilhetes e acompanhe seus resultados</p>
          </div>
          <Link
            to="/gerar-apostas"
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            Novo Jogo
          </Link>
          {games.length > 0 && (
            <button onClick={generateSelectedPDF} disabled={Object.keys(selected).filter(id=>selected[id]).length===0} className="px-6 py-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
              <i className="ri-file-pdf-line mr-2"></i>
              PDF Selecionados
            </button>
          )}
          {games.length > 0 && (
            <button onClick={checkSelected} disabled={Object.keys(selected).filter(id=>selected[id]).length===0} className="ml-3 px-6 py-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
              <i className="ri-flag-line mr-2"></i>
              Conferir Selecionados
            </button>
          )}
        </div>

        {/* Filtros */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-8">
          <h3 className="text-lg font-bold mb-4">Filtros</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Loteria</label>
              <select
                value={filter.lottery_type}
                onChange={(e) => setFilter({...filter, lottery_type: e.target.value})}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">Todas</option>
                {Object.entries(lotteryNames).map(([key, name]) => (
                  <option key={key} value={key}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select
                value={filter.status}
                onChange={(e) => setFilter({...filter, status: e.target.value})}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">Todos</option>
                <option value="pending">Pendente</option>
                <option value="checked">Conferido</option>
                <option value="expired">Expirado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">De</label>
              <input
                type="date"
                value={filter.date_from}
                onChange={(e) => setFilter({...filter, date_from: e.target.value})}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Até</label>
              <input
                type="date"
                value={filter.date_to}
                onChange={(e) => setFilter({...filter, date_to: e.target.value})}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>
        </div>

        {/* Lista de Jogos */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-cyan-400 animate-spin mb-4"></i>
              <p className="text-white/60">Carregando jogos...</p>
            </div>
          </div>
        ) : games.length === 0 ? (
          <div className="text-center py-12">
            <i className="ri-clover-line text-6xl text-white/20 mb-4"></i>
            <h3 className="text-xl font-bold mb-2">Nenhum jogo encontrado</h3>
            <p className="text-white/60 mb-6">Comece criando seu primeiro jogo!</p>
            <Link
              to="/gerar-apostas"
              className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line mr-2"></i>
              Criar Primeiro Jogo
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {games.map((game) => (
              <div key={game.id} className="relative group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all">
                <button
                  onClick={() => deleteGame(game)}
                  className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 translate-y-[-6px] group-hover:translate-y-0 transition-all duration-200 bg-red-500/80 hover:bg-red-600 text-white w-9 h-9 rounded-full flex items-center justify-center shadow-lg"
                  title="Excluir"
                >
                  <i className="ri-delete-bin-6-line"></i>
                </button>
                <button
                  onClick={() => { setContestModal({ open: true, game }); setContestInput(game.contest || ''); }}
                  className="absolute top-3 right-14 opacity-0 group-hover:opacity-100 translate-y-[-6px] group-hover:translate-y-0 transition-all duration-200 bg-cyan-500/80 hover:bg-cyan-600 text-white w-9 h-9 rounded-full flex items-center justify-center shadow-lg"
                  title="Definir concurso"
                >
                  <i className="ri-hashtag"></i>
                </button>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
                      <input
                        type="checkbox"
                        checked={!!selected[game.id]}
                        onChange={(e)=>setSelected({...selected, [game.id]: e.target.checked})}
                        className="w-5 h-5 accent-yellow-300"
                        aria-label="Selecionar jogo"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold">{lotteryNames[game.lottery_type as keyof typeof lotteryNames] || game.lottery_type}</h3>
                      {game.extra?.mega_virada && (
                        <span className="px-2 py-0.5 text-xs font-bold bg-green-600/30 text-green-300 border border-green-600/40 rounded-md">MEGA DA VIRADA</span>
                      )}
                    <p className="text-white/60 text-sm">Concurso {game.contest || 'Pendente'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 text-xs font-bold rounded-full border ${getStatusColor(game.status)}`}>
                      {getStatusLabel(game.status)}
                    </span>
                  </div>
                </div>

                {/* Números */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    {game.numbers.map((num, idx) => {
                      const matched = (checkFeedback[game.id]?.hits && (checkFeedback[game.id] as any)) ? (checkFeedback[game.id] as any).hits_numbers?.includes?.(num) : (game.hits_numbers || []).includes(num);
                      return (
                        <div key={idx} className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold ${matched ? 'bg-gradient-to-br from-green-500/30 to-emerald-500/30 border border-green-500/40 text-white' : 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30'}`}>
                          {num.toString().padStart(2, '0')}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Informações */}
                <div className="space-y-2 mb-4 text-sm">
                  {checkFeedback[game.id] && (
                    <div className={`mb-2 px-3 py-2 rounded-lg border ${checkFeedback[game.id].hits > 0 ? 'bg-green-500/15 border-green-500/30 text-green-400' : 'bg-white/10 border-white/20 text-white/70'}`}>
                      <span className="font-semibold">Conferido:</span>
                      <span className="ml-2">{checkFeedback[game.id].hits} acertos</span>
                      {typeof checkFeedback[game.id].prize !== 'undefined' && checkFeedback[game.id].prize! > 0 && (
                        <span className="ml-3">• Prêmio R$ {checkFeedback[game.id].prize!.toFixed(2)}</span>
                      )}
                    </div>
                  )}
                  {game.hits !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-white/60">Acertos:</span>
                      <span className="font-bold text-green-400">{game.hits}</span>
                    </div>
                  )}
                  {game.prize !== undefined && game.prize > 0 && (
                    <div className="flex justify-between">
                      <span className="text-white/60">Prêmio:</span>
                      <span className="font-bold text-yellow-400">R$ {game.prize.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-white/60">Criado:</span>
                    <span className="text-white/80">{new Date(game.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Ações */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => generatePDF(game)}
                    className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-all text-sm font-medium"
                  >
                    <i className="ri-file-pdf-line mr-1"></i>
                    PDF
                  </button>
                  {game.status === 'pending' && (game.draw_id || game.contest) ? (
                    <button
                      onClick={() => handleCheckNow(game)}
                      className="px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg hover:shadow-lg transition-all text-sm font-medium"
                    >
                      <i className="ri-check-line mr-1"></i>
                      Conferir
                    </button>
                  ) : game.status === 'pending' ? (
                    <button
                      onClick={() => { setContestModal({ open: true, game }); setContestInput(game.contest || ''); }}
                      className="px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg hover:shadow-lg transition-all text-sm font-medium"
                    >
                      <i className="ri-hashtag mr-1"></i>
                      Informar concurso
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDuplicate(game)}
                      className="px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg hover:shadow-lg transition-all text-sm font-medium"
                    >
                      <i className="ri-file-copy-line mr-1"></i>
                      Duplicar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {contestModal?.open && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 w-full max-w-md">
              <h3 className="text-lg font-bold mb-4">Definir Concurso</h3>
              <div className="space-y-3">
                <input
                  value={contestInput}
                  onChange={(e)=>setContestInput(e.target.value)}
                  placeholder="Número do concurso"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
                <div className="flex justify-end gap-2">
                  <button onClick={()=>{ setContestModal({ open: false, game: null }); setContestInput(''); }} className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg">Cancelar</button>
                  <button onClick={saveContest} className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg">Salvar</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Modal
// render outside return for patch simplicity; actual modal placed before closing container above
